import './register.scss'
import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { Form, Input, Button, Checkbox, Upload, Icon, notification } from 'antd'
import PropTypes from 'prop-types'
import { RegisterClientService } from './services'
import { validateCompanyRutHelper, validateRutHelper } from '../../helpers'

const Register = ({ history, form }) => {
    const { getFieldDecorator } = form

    const normalizeUploadFile = (event) => {
        if (Array.isArray(event)) {
            return event
        }

        return event && event.fileList
    }

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const [step, setStep] = useState(1)
    const [isSaving, setIsSaving] = useState(false)
    const [formData, setFormData] = useState({
        rut: '',
        nombres: '',
        apellidos: '',
        email: '',
        motivo: '',
        rutEmpresa: '',
        razonSocial: '',
        erutFile: null,
        acceptTerms: false,
    })

    const updateField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
    }

    const buildRegistrationFormData = (data) => {
        const payload = new FormData()
        payload.append('rut', data.rut || '')
        payload.append('name', data.nombres || '')
        payload.append('apPaterno', data.apellidos || '')
        payload.append('email', data.email || '')
        payload.append('motivo', data.motivo || '')
        payload.append('company.rut', data.rutEmpresa || '')
        payload.append('company.razonSocial', data.razonSocial || '')

        if (data.erutFile) {
            const fileObject = data.erutFile.originFileObj || data.erutFile
            payload.append('file', fileObject)
        }

        return payload
    }

    const saveRegistration = async () => {
        try {
            setIsSaving(true)
            const payload = buildRegistrationFormData(formData)
            const response = await RegisterClientService.save(payload)

            if (response && response.data && response.data.status === 'OK') {
                return true
            }

            notification.error({
                message: 'No se pudo completar el registro',
                description: 'Revisa los datos e intenta nuevamente.'
            })
            return false
        } catch (error) {
            notification.error({
                message: 'Error al registrar',
                description: 'Ocurrio un problema al guardar el registro. Intenta nuevamente.'
            })
            return false
        } finally {
            setIsSaving(false)
        }
    }

    const validateCurrentStep = () => {
        if (step === 3 && !formData.acceptTerms) {
            notification.error({
                message: 'Terminos y condiciones',
                description: 'Debes aceptar los terminos y condiciones para continuar.'
            })
            return false
        }

        return true
    }

    const goNext = () => {
        if (step === 1) {
            form.validateFields(['rut', 'nombres', 'apellidos', 'email', 'motivo'], (err, values) => {
                if (err) {
                    return
                }

                setFormData((prev) => ({ ...prev, ...values }))
                setStep(2)
            })

            return
        }

        if (step === 2) {
            form.validateFields(['rutEmpresa', 'razonSocial', 'erutFile'], (err, values) => {
                if (err) {
                    return
                }

                setFormData((prev) => ({
                    ...prev,
                    rutEmpresa: values.rutEmpresa,
                    razonSocial: values.razonSocial,
                    erutFile: values.erutFile && values.erutFile.length > 0 ? values.erutFile[0] : null,
                }))
                setStep(3)
            })

            return
        }

        if (!validateCurrentStep()) {
            return
        }

        if (step === 3) {
            saveRegistration().then((isSaved) => {
                if (isSaved) {
                    setStep(4)
                }
            })
            return
        }

        setStep((prev) => Math.min(prev + 1, 4))
    }

    const goBack = () => {
        setStep((prev) => Math.max(prev - 1, 1))
    }

    const finishRegistration = () => {
        history.push('/login')
    }

    const renderStep1 = () => (
        <>
            <h2>Paso 1: Ingresa tus datos</h2>
            <div className="register-grid">
                <Form.Item>
                    {getFieldDecorator('rut', {
                        initialValue: formData.rut,
                        rules: [{ required: true, message: 'El Rut es obligatorio' }],
                    })(
                        <Input placeholder="Rut" />
                    )}
                </Form.Item>
                <Form.Item>
                    {getFieldDecorator('nombres', {
                        initialValue: formData.nombres,
                        rules: [{ required: true, message: 'El nombre es obligatorio' }],
                    })(
                        <Input placeholder="Nombre(s)" />
                    )}
                </Form.Item>
                <Form.Item>
                    {getFieldDecorator('apellidos', {
                        initialValue: formData.apellidos,
                        rules: [{ required: true, message: 'Los apellidos son obligatorios' }],
                    })(
                        <Input placeholder="Apellidos" />
                    )}
                </Form.Item>
                <Form.Item>
                    {getFieldDecorator('email', {
                        initialValue: formData.email,
                        rules: [
                            { required: true, message: 'El email es obligatorio' },
                            { type: 'email', message: 'El formato del email no es valido' }
                        ],
                    })(
                        <Input type="email" placeholder="Email" />
                    )}
                </Form.Item>
                <Form.Item>
                    {getFieldDecorator('motivo', {
                        initialValue: formData.motivo,
                        rules: [{ required: true, message: 'El motivo es obligatorio' }],
                    })(
                        <Input.TextArea rows={4} placeholder="Motivo o finalidad de los datos adquiridos" />
                    )}
                </Form.Item>
            </div>
            <div className="register-actions single">
                <Button type="primary" onClick={goNext}>Siguiente</Button>
            </div>
        </>
    )

    const renderStep2 = () => (
        <>
            <h2>Paso 2: Ingresa los datos de la empresa que representas</h2>
            <p className="register-help-text">
                Te pedimos estos datos para validar la representacion de la empresa y resguardar un uso seguro de la plataforma.
            </p>
            <div className="register-grid">
                <Form.Item>
                    {getFieldDecorator('rutEmpresa', {
                        initialValue: formData.rutEmpresa,
                        rules: [
                            { required: true, message: 'El Rut de la empresa es obligatorio' },
                            { validator: (_, value) => {
                                if(value && !validateCompanyRutHelper(value)) {
                                    return Promise.reject('El Rut de la empresa no es valido')
                                }
                                return Promise.resolve();
                             }
                            }
                        ],
                    })(
                        <Input placeholder="Rut (Empresa)" />
                    )}
                </Form.Item>
                <Form.Item>
                    {getFieldDecorator('razonSocial', {
                        initialValue: formData.razonSocial,
                        rules: [{ required: true, message: 'La razon social es obligatoria' }],
                    })(
                        <Input placeholder="Razon social" />
                    )}
                </Form.Item>
                <Form.Item label="Adjuntar E-Rut (PDF) de la empresa emitido por SII">
                    {getFieldDecorator('erutFile', {
                        initialValue: formData.erutFile ? [formData.erutFile] : [],
                        valuePropName: 'fileList',
                        getValueFromEvent: normalizeUploadFile,
                        rules: [{ required: true, message: 'Debes adjuntar el E-Rut en PDF' }],
                    })(
                        <Upload
                            accept=".pdf,application/pdf"
                            beforeUpload={(file) => {
                                if (!file.name.toLowerCase().endsWith('.pdf')) {
                                    notification.error({
                                        message: 'Archivo invalido',
                                        description: 'Solo se permiten archivos PDF.'
                                    })
                                    return false
                                }

                                return false
                            }}
                        >
                            <Button>
                                <Icon type="upload" /> Seleccionar PDF
                            </Button>
                        </Upload>
                    )}
                </Form.Item>
                {formData.erutFile ? <p className="file-selected">Archivo seleccionado: {formData.erutFile.name}</p> : null}
            </div>
            <div className="register-actions">
                <Button onClick={goBack}>Anterior</Button>
                <Button type="primary" onClick={goNext} loading={isSaving}>Siguiente</Button>
            </div>
        </>
    )

    const renderStep3 = () => (
        <>
            <h2>Paso 3: Terminos y condiciones</h2>
            <div className="terms-box">
                <p>
                    Al usar esta tienda, aceptas que los reportes de compliance son de uso informativo para
                    procesos de debida diligencia y cumplimiento normativo.
                </p>
                <p>
                    Los datos entregados deben ser veraces. El acceso a los reportes es digital e inmediato,
                    y una vez disponible la descarga se entiende prestado el servicio.
                </p>
                <p>
                    {formData.razonSocial} trata datos personales conforme a la Ley N 19.628 y aplica las reglas
                    de proteccion al consumidor de la Ley N 19.496 en lo que corresponda.
                </p>
                <p>
                    El uso de la informacion es responsabilidad del cliente y no reemplaza asesoria legal.
                    El uso indebido de la plataforma puede implicar suspension de la cuenta.
                </p>
            </div>
            <Checkbox
                checked={formData.acceptTerms}
                onChange={({ target }) => updateField('acceptTerms', target.checked)}
            >
                He leido y acepto los terminos y condiciones de uso
            </Checkbox>
            <div className="register-actions">
                <Button onClick={goBack}>Anterior</Button>
                <Button type="primary" onClick={goNext}>Siguiente</Button>
            </div>
        </>
    )

    const renderStep4 = () => (
        <>
            <h2>Paso 4: Registro finalizado</h2>
            <p className="register-final-text">
                Gracias por registrarse, hemos enviado un correo electronico a la casilla ingresada,
                con las credenciales necesarias para iniciar el servicio.
            </p>
            <div className="register-actions single">
                <Button type="primary" onClick={finishRegistration}>Finalizar</Button>
            </div>
        </>
    )

    return (
        <div className="register-page">
            <div className="register-card">
                <Form layout="vertical">
                    <h1 className="register-title">Registro de cuenta</h1>
                    <div className="register-step-counter">Paso {step} de 4</div>
                    {step === 1 && renderStep1()}
                    {step === 2 && renderStep2()}
                    {step === 3 && renderStep3()}
                    {step === 4 && renderStep4()}
                </Form>
            </div>
        </div>
    )
}

Register.propTypes = {
    history: PropTypes.object.isRequired,
    form: PropTypes.object.isRequired,
}

export default withRouter(Form.create()(Register))